import { memo, useCallback, useMemo, useState } from "react";
import "bootstrap-icons/font/bootstrap-icons.css";
import { Button, Row, Spinner } from "react-bootstrap";
import { BsPlusLg } from "react-icons/bs";
import { MdOutlineEditNote } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useGetMyAttendeeNotes } from "../../api/queries/useGetMyAttendeeNotes";
import { useDeleteAttendeeNote } from "../../api/mutations/useDeleteAttendeeNote";
import { useSaveAttendeeNote } from "../../api/mutations/useSaveAttendeeNote";
import { useAppContext } from "../../Context/AppContext";
import { AttendeeNote } from "../../types/Types";
import AttendeeNoteCard from "./AttendeeNoteCard";
import {
  AttendeeNoteEditModalMemo,
  AttendeeNoteViewModalMemo,
} from "./AttendeeNoteModals";
import AttendeeNotesToolbar from "./AttendeeNotesToolbar";
import SaveAttendeeNoteModal from "./SaveAttendeeNoteModal";
import {
  CARD_SHADOW,
  PRIMARY_BTN_STYLE,
  SECONDARY_BTN_STYLE,
  buildSelfUser,
  filterAttendeeNotes,
  getEventOptionsFromNotes,
  type NoteTypeFilter,
} from "./attendeeNotesShared";

function AttendeeNotes() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { objectId, user: currentUser } = useAppContext();
  const { data, isLoading, isError, refetch } = useGetMyAttendeeNotes();
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<NoteTypeFilter>("");
  const [eventFilter, setEventFilter] = useState("");
  const [eventMenuSearch, setEventMenuSearch] = useState("");
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [editing, setEditing] = useState<AttendeeNote | null>(null);
  const [viewing, setViewing] = useState<AttendeeNote | null>(null);
  const [editText, setEditText] = useState("");
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [showAddOwnNote, setShowAddOwnNote] = useState(false);

  const notes = data ?? [];
  const selfUser = useMemo(
    () => buildSelfUser(currentUser?.user, objectId),
    [currentUser?.user, objectId]
  );
  const eventOptions = useMemo(() => getEventOptionsFromNotes(notes), [notes]);
  const filtered = useMemo(
    () =>
      filterAttendeeNotes(notes, {
        searchTerm,
        typeFilter,
        eventFilter,
        personalLabel: t("attendeeNotes.personalBadge"),
        youLabel: t("attendeeNotes.you"),
      }),
    [eventFilter, notes, searchTerm, t, typeFilter]
  );

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["attendeeNotes"] });
  };

  const { mutate: saveNote, isPending: saving } = useSaveAttendeeNote({
    onSuccess: () => {
      toast.success(t("attendeeNotes.updatedSuccess"));
      invalidate();
      setEditing(null);
    },
    onError: () => toast.error(t("attendeeNotes.saveError")),
  });

  const { mutate: deleteNote, isPending: deleting } = useDeleteAttendeeNote({
    onSuccess: () => {
      toast.success(t("attendeeNotes.deletedSuccess"));
      setDeletingId(null);
      invalidate();
    },
    onError: () => {
      toast.error(t("attendeeNotes.deleteError"));
      setDeletingId(null);
    },
  });

  const openEdit = useCallback((note: AttendeeNote) => {
    setViewing(null);
    setEditing(note);
    setEditText(note.noteText);
  }, []);

  const openView = useCallback((note: AttendeeNote) => setViewing(note), []);

  const handleOpenAddOwn = useCallback(() => {
    if (!selfUser?.objectId) {
      toast.error(t("attendeeNotes.attendeeMissing"));
      return;
    }
    setShowAddOwnNote(true);
  }, [selfUser?.objectId, t]);

  const handleSaveEdit = () => {
    if (!editing) return;
    const text = editText.trim();
    if (!text) {
      toast.error(t("attendeeNotes.noteRequired"));
      return;
    }
    saveNote({ id: editing.id, noteText: text });
  };

  const handleDelete = useCallback(
    (id: number) => {
      setDeletingId(id);
      deleteNote(id);
    },
    [deleteNote]
  );

  return (
    <main
      className="page-content w-100 pb-4 d-flex flex-column min-vh-100"
      aria-label={t("attendeeNotes.pageAria")}
    >
      <div className="d-flex align-items-center gap-3 mb-4 p-3 px-md-4 py-3 py-md-4 rounded-top bg-project-gradient text-white shadow-sm">
        <div className="rounded-circle bg-white bg-opacity-25 p-3 d-flex align-items-center justify-content-center flex-shrink-0">
          <MdOutlineEditNote size={24} aria-hidden />
        </div>
        <div className="min-w-0">
          <h5 className="mb-1 fw-bold text-white">{t("attendeeNotes.title")}</h5>
          <p className="mb-0 small text-white-50">{t("attendeeNotes.subtitle")}</p>
        </div>
      </div>

      <AttendeeNotesToolbar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
        eventFilter={eventFilter}
        onEventFilterChange={setEventFilter}
        eventOptions={eventOptions}
        eventMenuSearch={eventMenuSearch}
        onEventMenuSearchChange={setEventMenuSearch}
        showMobileFilters={showMobileFilters}
        onToggleMobileFilters={() => setShowMobileFilters((p) => !p)}
        canAddNote={!!selfUser?.objectId}
        onAddNote={handleOpenAddOwn}
        onClearFilters={() => {
          setSearchTerm("");
          setTypeFilter("");
          setEventFilter("");
          setEventMenuSearch("");
        }}
      />

      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t("feed.loading")}</span>
          </Spinner>
        </div>
      ) : isError ? (
        <div className="bg-white rounded-3 p-4 text-center" style={CARD_SHADOW}>
          <p className="text-danger mb-3">{t("attendeeNotes.loadError")}</p>
          <Button
            className="rounded-2 border-0 shadow-sm fw-medium text-white"
            style={PRIMARY_BTN_STYLE}
            onClick={() => refetch()}
          >
            {t("attendeeNotes.retry")}
          </Button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-3 p-5 text-center" style={CARD_SHADOW}>
          <MdOutlineEditNote
            size={48}
            className="text-secondary mb-3 opacity-75"
            aria-hidden
          />
          {notes.length > 0 &&
          (searchTerm.trim() || typeFilter || eventFilter) ? (
            <>
              <h6 className="fw-semibold mb-2">
                {t("attendeeNotes.noResultsTitle")}
              </h6>
              <p className="text-muted small mb-3">
                {t("attendeeNotes.noResultsHint")}
              </p>
              <Button
                className="rounded-2 border-0 shadow-sm fw-medium text-dark"
                style={SECONDARY_BTN_STYLE}
                onClick={() => {
                  setSearchTerm("");
                  setTypeFilter("");
                  setEventFilter("");
                  setEventMenuSearch("");
                }}
              >
                {t("attendeeNotes.clearFilters")}
              </Button>
            </>
          ) : (
            <>
              <h6 className="fw-semibold mb-2">
                {t("attendeeNotes.emptyTitle")}
              </h6>
              <p className="text-muted small mb-3">
                {t("attendeeNotes.emptyHint")}
              </p>
              <div className="d-flex flex-wrap justify-content-center gap-2">
                <Button
                  className="rounded-2 border-0 shadow-sm fw-medium text-white d-inline-flex align-items-center gap-2"
                  style={PRIMARY_BTN_STYLE}
                  onClick={handleOpenAddOwn}
                  disabled={!selfUser?.objectId}
                >
                  <BsPlusLg aria-hidden size={14} />
                  {t("attendeeNotes.addOwn")}
                </Button>
                <Button
                  className="rounded-2 border-0 shadow-sm fw-medium text-dark"
                  style={SECONDARY_BTN_STYLE}
                  onClick={() => navigate("/event-booking")}
                >
                  {t("attendeeNotes.goToEvents")}
                </Button>
              </div>
            </>
          )}
        </div>
      ) : (
        <Row className="g-3">
          {filtered.map((note) => (
            <AttendeeNoteCard
              key={note.id}
              note={note}
              locale={locale}
              selfObjectId={selfUser?.objectId}
              deletingId={deletingId}
              deleting={deleting}
              onEdit={openEdit}
              onView={openView}
              onDelete={handleDelete}
            />
          ))}
        </Row>
      )}

      <AttendeeNoteViewModalMemo
        note={viewing}
        selfObjectId={selfUser?.objectId}
        onHide={() => setViewing(null)}
        onEdit={openEdit}
      />
      <AttendeeNoteEditModalMemo
        note={editing}
        selfObjectId={selfUser?.objectId}
        editText={editText}
        onEditTextChange={setEditText}
        saving={saving}
        onHide={() => setEditing(null)}
        onSave={handleSaveEdit}
      />
      {selfUser ? (
        <SaveAttendeeNoteModal
          show={showAddOwnNote}
          onHide={() => setShowAddOwnNote(false)}
          eventId={0}
          eventName={t("attendeeNotes.personalBadge")}
          attendee={selfUser}
          alwaysCreate
        />
      ) : null}
    </main>
  );
}

export default memo(AttendeeNotes);
